/**
 * 카드 에디션(언어판) 선택 상태 — PKA 화면정의서 SC-009.
 *
 * IA v0.1(rev) 진입 플로우 개정: **검색 진입 시 에디션을 먼저 고르고, 그 선택을
 * 기준으로 카드를 표시한다.** 즉 에디션은 한 화면의 로컬 토글이 아니라 탐색 전체를
 * 관통하는 상태다 — 검색에서 '일본판'을 골랐는데 카드 상세가 한국판 시세를 보여주면
 * 그 자체로 앱이 거짓말을 하는 셈이 된다.
 *
 * 그래서 화면마다 useState 로 두지 않고 여기 한 곳에 둔다.
 * 선택은 localStorage 에 남겨 앱을 다시 열어도 유지된다 (기본값 한국판, PRD §3.1).
 *
 * ⚠ 이건 표시 기준 상태이지 서버 상태가 아니다. 로그인/세션과 무관하므로
 * `src/store` 가 아니라 컴포넌트 계층에 둔다.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { EDITIONS, type Edition } from "../../data/priceModel";

const STORAGE_KEY = "peeka.cardEdition";

/** PRD §3.1 — 기본값은 한국판. */
const DEFAULT_EDITION: Edition = "ko";

interface EditionContextValue {
  edition: Edition;
  setEdition: (edition: Edition) => void;
}

const EditionContext = createContext<EditionContextValue | null>(null);

function loadEdition(): Edition {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return EDITIONS.includes(raw as Edition) ? (raw as Edition) : DEFAULT_EDITION;
  } catch {
    return DEFAULT_EDITION;
  }
}

export function EditionProvider({ children }: { children: ReactNode }) {
  const [edition, setEditionState] = useState<Edition>(loadEdition);

  const setEdition = useCallback((next: Edition) => {
    setEditionState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage 불가 환경 무시 — 이번 세션 동안만 유지된다 */
    }
  }, []);

  const value = useMemo(() => ({ edition, setEdition }), [edition, setEdition]);
  return <EditionContext.Provider value={value}>{children}</EditionContext.Provider>;
}

/**
 * 현재 선택된 카드 에디션. Provider 밖에서 호출하면 기본값(한국판)으로 동작하고
 * 변경은 무시한다 — 테스트가 페이지를 단독 렌더할 때 터지지 않게 하기 위함.
 */
export function useEdition(): EditionContextValue {
  const ctx = useContext(EditionContext);
  return ctx ?? { edition: DEFAULT_EDITION, setEdition: () => undefined };
}
